// askSmart-ui/src/pages/TOSP/ManageTOSP.tsx
import React, { useMemo, useState } from "react";
import "./ManageTOSP.css";
import { TospChromeProvider } from "./TospChromeContext";
import { TospDataProvider, useTospData } from "./TospDataContext";
import TospStatusBar from "./components/TospStatusBar";
import TospTrafficLights from "./components/TospTrafficLights";
import OverviewTab from "./tabs/OverviewTab";
import BuildingsTab from "./tabs/BuildingsTab";
import UnitsTab from "./tabs/UnitsTab";
import TenantsTab from "./tabs/TenantsTab";
import PastTenantsTab from "./tabs/PastTenantsTab";
import TaxRollsTab from "./tabs/TaxRollsTab";
import ConfigurationTab from "./tabs/ConfigurationTab";
import BudgetTab from "./tabs/BudgetTab";
import EstoppelsTab from "./tabs/EstoppelsTab";
import TransferTab from "./tabs/TransferTab";
import AuditTab from "./tabs/AuditTab";
import { redCountsByTab, redGroups } from "./tospTraffic";
import { currentTenants, pastTenants } from "./types";

export type TospTab =
  | "overview"
  | "configuration"
  | "buildings"
  | "units"
  | "tenants"
  | "pastTenants"
  | "taxRolls"
  | "budget"
  | "estoppels"
  | "transfer"
  | "audit";

// The module's top-level sections, in the order they're shown. Each entry owns
// its own component, so a section can grow (toolbar, table, modals) without
// touching this shell. The first entry is what the page opens on.
const TABS: { id: TospTab; label: string; Panel: React.ComponentType }[] = [
  { id: "overview", label: "Overview", Panel: OverviewTab },
  { id: "configuration", label: "Configuration", Panel: ConfigurationTab },
  { id: "buildings", label: "Buildings", Panel: BuildingsTab },
  { id: "units", label: "Units", Panel: UnitsTab },
  { id: "tenants", label: "Tenants", Panel: TenantsTab },
  { id: "pastTenants", label: "Past Tenants", Panel: PastTenantsTab },
  { id: "taxRolls", label: "Tax Rolls", Panel: TaxRollsTab },
  { id: "budget", label: "Budget", Panel: BudgetTab },
  { id: "estoppels", label: "Estoppels", Panel: EstoppelsTab },
  { id: "transfer", label: "Transfer", Panel: TransferTab },
  { id: "audit", label: "Audit Logs", Panel: AuditTab },
];

// Admin side of TOSP (/managetosp). The user-facing portal (/tosp) is a
// separate entry point and shares nothing but the module's data.
export default function ManageTOSP() {
  return (
    // The module's collections are provided once, above the tabs, so switching
    // tabs never remounts the data and every section sees the same records.
    <TospDataProvider>
      <ManageTospContent />
    </TospDataProvider>
  );
}

// Inside the provider, so the tabs can wait for the first load rather than
// rendering empty grids against data that hasn't arrived.
function ManageTospContent() {
  const { ready, error, clearError, buildings, units, tenants, taxRolls, configuration } =
    useTospData();
  const [activeTab, setActiveTab] = useState<TospTab>(TABS[0].id);
  // Set by a tab that has drilled into a single record and wants the page to
  // itself — see TospChromeContext.
  const [chromeHidden, setChromeHidden] = useState(false);
  // A record a tab should open on, set by the traffic lights' Go. See TospChrome.
  const [focus, setFocus] = useState<string | null>(null);
  const active = TABS.find((t) => t.id === activeTab) || TABS[0];
  const ActivePanel = active.Panel;

  // Stable, so a tab's effect doesn't re-run on every render of this shell.
  const chrome = useMemo(
    () => ({ setChromeHidden, focus, clearFocus: () => setFocus(null) }),
    [focus],
  );

  // How much each collection holds, shown on its tab. Only the four that ARE a
  // collection — Overview, Budget and the rest count nothing, and a badge on
  // them would invite the question of what it counted.
  // Tenants counts the businesses still trading, and Past Tenants the ones that
  // have left, so the two add up to the collection rather than overlapping. A
  // Tenants badge that included departed tenancies would disagree with its own
  // grid, which no longer lists them.
  const current = useMemo(() => currentTenants(tenants), [tenants]);
  const past = useMemo(() => pastTenants(tenants), [tenants]);

  const counts: Partial<Record<TospTab, number>> = {
    buildings: buildings.length,
    units: units.length,
    tenants: current.length,
    pastTenants: past.length,
    taxRolls: taxRolls.length,
  };

  // A counter goes red when something on that tab is waiting on the admin —
  // an unassigned roll, a unit nobody holds, a tenant record with a gap in it.
  // Read from the SAME rules the red light counts, so the two can never
  // disagree about whether there is anything to do.
  const issues = useMemo(
    () => redCountsByTab(redGroups(configuration, tenants, taxRolls, units)),
    [configuration, tenants, taxRolls, units],
  );

  return (
    <TospChromeProvider value={chrome}>
      <div className="tosp-page">
        {/* A REFUSED WRITE HAS TO BE SEEN. The provider has recorded save
            failures all along and nothing rendered them, so a save the server
            turned down — an ETag clash, a budget already broadcasting that
            year, a name already in use — looked exactly like one that worked,
            until the page was reloaded and the change had gone. Shown above
            everything, because it belongs to the module rather than to
            whichever tab happens to be open. */}
        {error && (
          <div className="tosp-banner tosp-banner-error" role="alert">
            <span>{error}</span>
            <button type="button" className="tosp-btn-secondary" onClick={clearError}>
              Dismiss
            </button>
          </div>
        )}

        {!chromeHidden && (
          <>
            <h1 className="tosp-title">Manage TOSP</h1>

            {/* Above the tabs so the work waiting is seen before a tab is
                chosen — and so a task can send the admin to the tab that
                fixes it. */}
            <TospTrafficLights
              onGoTo={(tab, wanted) => {
                setActiveTab(tab);
                // Set alongside the tab, so the panel mounts with it already
                // there rather than having to react to it arriving late.
                setFocus(wanted ?? null);
              }}
            />

            <TospStatusBar />

            <div className="tosp-tabs" role="tablist" aria-label="TOSP sections">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  id={`tosp-tab-${tab.id}`}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === tab.id}
                  aria-controls={`tosp-panel-${tab.id}`}
                  className={`tosp-tab${activeTab === tab.id ? " tosp-tab-active" : ""}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                  {/* Gated on `ready`: the collections are empty until the
                      first load lands, and a row of zeros that turns into the
                      real figures reads as data that changed rather than data
                      that arrived. */}
                  {ready && counts[tab.id] !== undefined && (
                    <span
                      className={`tosp-tab-count${
                        issues[tab.id] ? " tosp-tab-count-issue" : ""
                      }`}
                      title={
                        issues[tab.id]
                          ? `${issues[tab.id]} thing${
                              issues[tab.id] === 1 ? "" : "s"
                            } here need attention — see the red light above`
                          : undefined
                      }
                    >
                      {counts[tab.id]}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </>
        )}

        <div
          id={`tosp-panel-${active.id}`}
          role="tabpanel"
          aria-labelledby={`tosp-tab-${active.id}`}
        >
          {ready ? (
            <ActivePanel />
          ) : (
            <section className="tosp-card">
              <div className="tosp-empty">
                <div className="tosp-empty-title">Loading…</div>
              </div>
            </section>
          )}
        </div>
      </div>
    </TospChromeProvider>
  );
}
